import React, { useEffect, useState, useMemo } from "react";
import { Button, Card, Col, Row, Modal, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import PageLoader from "../components/PageLoader";
import { sourcesApi } from "../api/sources";

// ============================================================
// Helpers
// ============================================================
const slugify = (str) =>
  str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").replace(/-+/g, "-");

const EMPTY_FORM = {
  name: "",
  slug: "",
  type: "government",
  website_url: "",
  logo_url: "",
};

const SOURCE_TYPES = [
  { value: "government", label: "Gouvernement" },
  { value: "international", label: "International" },
  { value: "civil_society", label: "Société civile" },
  { value: "electoral_body", label: "Organe électoral" },
  { value: "other", label: "Autre" },
];

export default function SourcesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [skin, setSkin] = useState(localStorage.getItem("skin-mode") ? "dark" : "");

  // --- Modals ---
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ============================================================
  // Chargement
  // ============================================================
  const loadData = () => {
    setLoading(true);
    sourcesApi
      .list()
      .then((data) => setItems(data || []))
      .catch((err) => console.error("Erreur chargement sources:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  // ============================================================
  // Filtrage + pagination
  // ============================================================
  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.slug?.toLowerCase().includes(q) ||
        s.type?.toLowerCase().includes(q)
    );
  }, [items, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, safePage, perPage]);

  useEffect(() => { setCurrentPage(1); }, [search, perPage]);

  // ============================================================
  // Ouverture Modals
  // ============================================================
  const openCreate = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setFormOpen(true);
  };

  const openEdit = (src) => {
    setEditingId(src.id);
    setFormData({
      name: src.name || "",
      slug: src.slug || "",
      type: src.type || "government",
      website_url: src.websiteUrl || src.website_url || "",
      logo_url: src.logoUrl || src.logo_url || "",
    });
    setFormErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
  };

  // ============================================================
  // Changements formulaire
  // ============================================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => {
      const next = { ...f, [name]: value };
      if (name === "name" && !editingId && (!f.slug || f.slug === slugify(f.name))) {
        next.slug = slugify(value);
      }
      return next;
    });
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // ============================================================
  // Soumission
  // ============================================================
  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = "Le nom est requis.";
    if (!formData.slug.trim()) errs.slug = "Le slug est requis.";
    if (formData.slug && !/^[a-z0-9-]+$/.test(formData.slug)) {
      errs.slug = "Format invalide.";
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        type: formData.type,
        website_url: formData.website_url.trim() || null,
        logo_url: formData.logo_url.trim() || null,
      };
      if (editingId) {
        await sourcesApi.update(editingId, payload);
      } else {
        await sourcesApi.create(payload);
      }
      setFormOpen(false);
      loadData();
    } catch (err) {
      alert("Erreur : " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // Suppression
  // ============================================================
  const askDelete = (src) => setDeleteTarget({ id: src.id, name: src.name });

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await sourcesApi.destroy(deleteTarget.id);
      setItems((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      alert("Erreur lors de la suppression : " + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(false);
    }
  };

  // ============================================================
  // Loader
  // ============================================================
  if (loading) {
    return (
      <React.Fragment>
        <Header onSkin={setSkin} />
        <div className="main main-app cere-main p-3 p-lg-4">
          <PageLoader message="Chargement des sources…" />
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  // ============================================================
  // Rendu
  // ============================================================
  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app cere-main p-3 p-lg-4">

        {/* En-tête */}
        <div className="cere-page-head d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb cere-breadcrumb fs-sm mb-1">
                <li className="breadcrumb-item"><Link to="/dashboard">Admin</Link></li>
                <li className="breadcrumb-item active">Sources</li>
              </ol>
            </nav>
            <h4 className="cere-page-title mb-0">Sources institutionnelles</h4>
            <p className="cere-page-subtitle mb-0">
              Gérez les institutions émettrices des documents (Ministère, Cour, ONG…)
            </p>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Button className="cere-btn cere-btn-primary" onClick={openCreate}>
              <i className="ri-add-line me-2"></i>
              Nouvelle source
            </Button>
          </div>
        </div>

        {/* Toolbar */}
        <Card className="cere-card cere-toolbar-card mb-4">
          <Card.Body>
            <Row className="g-3 align-items-center">
              <Col md="7">
                <div className="cere-search-input">
                  <i className="ri-search-line"></i>
                  <input
                    type="text"
                    placeholder="Rechercher une source…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      className="cere-search-clear"
                      onClick={() => setSearch("")}
                      aria-label="Effacer"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              </Col>
              <Col md="5" className="d-flex align-items-center gap-2 justify-content-md-end">
                <span className="cere-toolbar-label">Afficher</span>
                <select
                  className="cere-select"
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="cere-toolbar-label">par page</span>
                <span className="cere-toolbar-label ms-3">
                  {filtered.length} source{filtered.length > 1 ? "s" : ""}
                </span>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tableau */}
        {filtered.length === 0 ? (
          <Card className="cere-card cere-empty-state">
            <Card.Body>
              <div className="cere-empty-icon">
                <i className="ri-building-bank-line"></i>
              </div>
              <h5 className="cere-empty-title">Aucune source</h5>
              <p className="cere-empty-desc">
                {search
                  ? `Aucun résultat pour « ${search} ».`
                  : "Commencez par ajouter une source institutionnelle."}
              </p>
              <Button className="cere-btn cere-btn-primary" onClick={openCreate}>
                <i className="ri-add-line me-2"></i>
                Créer une source
              </Button>
            </Card.Body>
          </Card>
        ) : (
          <Card className="cere-card cere-table-card">
            <div className="table-responsive">
              <table className="cere-table-docs">
                <thead>
                  <tr>
                    <th style={{ width: 56 }}>#</th>
                    <th>Nom</th>
                    <th>Type</th>
                    <th>Site web</th>
                    <th style={{ width: 120 }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((src) => (
                    <tr key={src.id}>
                      <td><span className="cere-doc-id">#{src.id}</span></td>
                      <td>
                        <div className="cere-doc-info">
                          <div className="cere-doc-info-icon">
                            <i className="ri-building-bank-line"></i>
                          </div>
                          <div className="cere-doc-info-text">
                            <span className="cere-doc-info-title has-tooltip" data-tooltip={src.name}>
                              {src.name}
                            </span>
                            <div className="cere-doc-info-slug">{src.slug}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`cere-chip cere-chip--${
                          src.type === "government" ? "blue" :
                          src.type === "international" ? "green" :
                          src.type === "civil_society" ? "yellow" : "ink"
                        }`}>
                          {SOURCE_TYPES.find(t => t.value === src.type)?.label || src.type}
                        </span>
                      </td>
                      <td>
                        {src.websiteUrl ? (
                          <a href={src.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                            <i className="ri-external-link-line me-1"></i>Lien
                          </a>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-end">
                        <div className="cere-doc-actions">
                          <button
                            className="cere-icon-btn"
                            onClick={() => openEdit(src)}
                            title="Éditer"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            className="cere-icon-btn cere-icon-btn--danger"
                            onClick={() => askDelete(src)}
                            title="Supprimer"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="cere-pagination-wrapper">
            <div className="cere-pagination">
              <button
                className="cere-page-btn"
                disabled={safePage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {pageNumbers({ totalPages, safePage }).map((p, i) =>
                p === "…" ? (
                  <span key={`e-${i}`} className="cere-page-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    className={`cere-page-btn ${p === safePage ? "active" : ""}`}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                className="cere-page-btn"
                disabled={safePage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
            <div className="cere-pagination-info">
              Page {safePage} sur {totalPages}
            </div>
          </div>
        )}

        {/* ============================================================
            MODAL CRÉATION / ÉDITION
            ============================================================ */}
        <Modal
          show={formOpen}
          onHide={closeForm}
          centered
          size="lg"
          backdrop="static"
          className="cere-modal-form"
        >
          <Modal.Header closeButton={!saving}>
            <Modal.Title>
              <i className={editingId ? "ri-edit-line" : "ri-add-line"}></i>
              {editingId ? "Modifier la source" : "Nouvelle source"}
            </Modal.Title>
          </Modal.Header>

          <form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row className="g-3">
                <Col md="8">
                  <label className="cere-form-label">
                    Nom <span className="cere-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`cere-form-control ${formErrors.name ? "is-invalid" : ""}`}
                    placeholder="Ex : Ministère de l'Intérieur"
                    autoFocus
                  />
                  {formErrors.name && <div className="cere-form-error">{formErrors.name}</div>}
                </Col>

                <Col md="4">
                  <label className="cere-form-label">Type</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="cere-form-control"
                  >
                    {SOURCE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </Col>

                <Col md="12">
                  <label className="cere-form-label">
                    Slug <span className="cere-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className={`cere-form-control ${formErrors.slug ? "is-invalid" : ""}`}
                    placeholder="ministere-interieur"
                  />
                  {formErrors.slug ? (
                    <div className="cere-form-error">{formErrors.slug}</div>
                  ) : (
                    <div className="cere-form-help">
                      Identifiant unique (lettres minuscules, chiffres, tirets)
                    </div>
                  )}
                </Col>

                <Col md="12">
                  <label className="cere-form-label">Site web</label>
                  <input
                    type="url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    className="cere-form-control"
                    placeholder="https://exemple.com"
                  />
                </Col>

                <Col md="12">
                  <label className="cere-form-label">URL du logo</label>
                  <input
                    type="url"
                    name="logo_url"
                    value={formData.logo_url}
                    onChange={handleChange}
                    className="cere-form-control"
                    placeholder="https://exemple.com/logo.png"
                  />
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <button
                type="button"
                className="cere-btn cere-btn-outline"
                onClick={closeForm}
                disabled={saving}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="cere-btn cere-btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Enregistrement…
                  </>
                ) : (
                  <>
                    <i className="ri-save-line me-2"></i>
                    {editingId ? "Mettre à jour" : "Créer"}
                  </>
                )}
              </button>
            </Modal.Footer>
          </form>
        </Modal>

        {/* ============================================================
            MODAL SUPPRESSION
            ============================================================ */}
        <Modal
          show={!!deleteTarget}
          onHide={() => !deleting && setDeleteTarget(null)}
          centered
          backdrop="static"
          className="cere-modal-delete"
        >
          <div className="cere-modal-delete-icon">
            <i className="ri-delete-bin-line"></i>
          </div>
          <Modal.Body className="text-center">
            <h4 className="cere-modal-delete-title">Confirmer la suppression</h4>
            <p className="cere-modal-delete-text">
              Êtes-vous sûr de vouloir supprimer cette source ? Cette action est définitive.
            </p>
            {deleteTarget && (
              <div className="cere-modal-delete-preview">
                <i className="ri-building-bank-line"></i>
                <div className="cere-modal-delete-preview-content">
                  <span className="cere-modal-delete-preview-label">Source ciblée</span>
                  <span className="cere-modal-delete-preview-name">{deleteTarget.name}</span>
                </div>
              </div>
            )}
            <div className="cere-modal-delete-warning-box">
              <i className="ri-error-warning-line"></i>
              <span>Les documents liés à cette source perdront leur référence.</span>
            </div>
          </Modal.Body>
          <Modal.Footer className="cere-modal-delete-footer">
            <button
              className="cere-btn cere-btn-outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Annuler
            </button>
            <button
              className="cere-btn cere-btn-danger"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Suppression…
                </>
              ) : (
                <>
                  <i className="ri-delete-bin-line me-2"></i>
                  Supprimer
                </>
              )}
            </button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}

// ============================================================
// Helper de pagination
// ============================================================
function pageNumbers({ totalPages, safePage }) {
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (safePage > 3) pages.push("…");
    const start = Math.max(2, safePage - 1);
    const end = Math.min(totalPages - 1, safePage + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (safePage < totalPages - 2) pages.push("…");
    pages.push(totalPages);
  }
  return pages;
}